import React, { useState, useEffect } from 'react';
import {
  Table,
  Form,
  Select,
  Input,
  Button,
  Card,
  Col,
  message,
  DatePicker,
  Tag,
} from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import dayjs from 'dayjs';
import FilterPanel from '../../../components/common/FilterPanel';
import * as reportService from '../../../services/report/reportService';
import { ORDER_STATUS_OPTIONS } from '../../../constants/order';

const { RangePicker } = DatePicker;

// Is Timeout 筛选选项
const getIsTimeoutOptions = (t) => [
  { value: 'true', label: t('common.yes') },
  { value: 'false', label: t('common.no') },
];

// Order End Status 派生值 -> 订单域 ORDER_STATUS 枚举值映射，用于复用订单列表的文案与颜色
const ORDER_END_STATUS_TO_ORDER_STATUS = {
  COMPLETED: 'COMPLETED',
  OUTBOUND_COMPLETED: 'OUTBOUND_COMPLETED',
  Cancelled: 'CANCELLED',
  Canceled: 'CANCELLED',
  Returned: 'RETURN',
  'In Progress': 'IN_PROGRESS',
};

// antd 色值 -> 十六进制颜色，与订单列表页保持一致
const ORDER_STATUS_COLOR_MAP = {
  blue: '#1890ff',
  processing: '#1890ff',
  green: '#52c41a',
  red: '#ff4d4f',
  gold: '#faad14',
  default: '#8c8c8c',
};

// Order End Status 筛选选项（value 与后端状态码/分类保持一致，文案与订单状态保持一致）
const getOrderEndStatusOptions = (t) => [
  { value: 'COMPLETED', label: t('order.status.completed') },
  { value: 'OUTBOUND_COMPLETED', label: t('order.status.outboundCompleted') },
  { value: 'Canceled', label: t('order.status.cancelled') },
  { value: 'Returned', label: t('order.status.return') },
  { value: 'In Progress', label: t('order.status.inProgress') },
];

/**
 * 数据归一化：后端 DTO 格式转为 PRD 展示格式
 * Mock 数据已是 PRD 格式，后端数据需要计算
 */
const normalizeRecord = (record) => {
  // Mock 数据已包含 pickingTimeSpent，直接返回
  if (record.pickingTimeSpent !== undefined) {
    return { ...record, orderNo: record.orderId || record.orderNo };
  }

  // 后端 DTO 格式，计算各时间段分钟数
  const pickingStartMs = record.pickingStartTime ? dayjs(record.pickingStartTime).valueOf() : null;
  const pickingEndMs = record.pickingEndTime ? dayjs(record.pickingEndTime).valueOf() : null;
  const sortingCompleteMs = record.sortingCompleteTime ? dayjs(record.sortingCompleteTime).valueOf() : null;
  const deadlineMs = record.deadlineTime ? dayjs(record.deadlineTime).valueOf() : null;
  const orderCreateMs = record.orderCreateTime ? dayjs(record.orderCreateTime).valueOf() : null;

  const pickingTimeSpent = (pickingStartMs && pickingEndMs) ? (pickingEndMs - pickingStartMs) / 60000 : null;
  const sortPackTime = (pickingEndMs && sortingCompleteMs) ? (sortingCompleteMs - pickingEndMs) / 60000 : null;
  const slaDeadline = (orderCreateMs && deadlineMs) ? (deadlineMs - orderCreateMs) / 60000 : null;

  const endTime = sortingCompleteMs || pickingEndMs;
  const timeoutMargin = (endTime && deadlineMs) ? (endTime - deadlineMs) / 60000 : null;

  // 合并状态字段为单一 Order End Status（value 与后端状态码/分类保持一致）
  let orderEndStatus = 'In Progress';
  if (record.cancelLabel) orderEndStatus = 'Cancelled';
  else if (record.isReturn) orderEndStatus = 'Returned';
  else if (record.status === 'COMPLETED') orderEndStatus = 'COMPLETED';
  else if (record.status === 'OUTBOUND_COMPLETED') orderEndStatus = 'OUTBOUND_COMPLETED';

  return {
    ...record,
    pickingTimeSpent: pickingTimeSpent != null ? Math.round(pickingTimeSpent * 100) / 100 : null,
    sortPackTime: sortPackTime != null ? Math.round(sortPackTime * 100) / 100 : null,
    slaDeadline: slaDeadline != null ? Math.round(slaDeadline * 100) / 100 : null,
    timeoutMargin: timeoutMargin != null ? Math.round(timeoutMargin * 100) / 100 : null,
    orderEndStatus,
  };
};

const SLAFulfillmentReport = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`,
  });

  const columns = [
    {
      title: t('report.slaFulfillment.orderId'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      fixed: 'left',
      width: 200,
    },
    {
      title: t('report.slaFulfillment.channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (channel) => channel ? (
        <Tag color={channel === 'LOTUS' ? 'blue' : 'purple'}>{channel}</Tag>
      ) : '-',
    },
    {
      title: t('report.slaFulfillment.paymentMethod'),
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 160,
    },
    {
      title: t('report.slaFulfillment.orderCreateTime'),
      dataIndex: 'orderCreateTime',
      key: 'orderCreateTime',
      width: 180,
    },
    {
      title: t('report.slaFulfillment.pickingTime'),
      dataIndex: 'pickingTimeSpent',
      key: 'pickingTimeSpent',
      width: 150,
      align: 'right',
      render: (v) => v != null ? v.toFixed(2) : '-',
    },
    {
      title: t('report.slaFulfillment.sortPackTime'),
      dataIndex: 'sortPackTime',
      key: 'sortPackTime',
      width: 170,
      align: 'right',
      render: (v) => v != null ? v.toFixed(2) : '-',
    },
    {
      title: t('report.slaFulfillment.slaDeadline'),
      dataIndex: 'slaDeadline',
      key: 'slaDeadline',
      width: 150,
      align: 'right',
      render: (v) => v != null ? Math.round(v) : '-',
    },
    {
      title: t('report.slaFulfillment.timeout'),
      dataIndex: 'timeoutMargin',
      key: 'timeoutMargin',
      width: 140,
      align: 'right',
      render: (v) => {
        if (v == null) return '-';
        const color = v > 0 ? '#ff4d4f' : '#52c41a';
        const prefix = v > 0 ? '+' : '';
        return <span style={{ color, fontWeight: 500 }}>{prefix}{v.toFixed(2)}</span>;
      },
    },
    {
      title: t('report.slaFulfillment.orderEndStatus'),
      dataIndex: 'orderEndStatus',
      key: 'orderEndStatus',
      width: 150,
      render: (status) => {
        const orderStatusValue = ORDER_END_STATUS_TO_ORDER_STATUS[status];
        const option = ORDER_STATUS_OPTIONS.find((opt) => opt.value === orderStatusValue);
        const color = ORDER_STATUS_COLOR_MAP[option?.color] || '#8c8c8c';
        const displayText = option ? t(option.label) : (status || '-');
        return (
          <span style={{ color, fontWeight: 500 }} title={displayText}>
            {displayText}
          </span>
        );
      },
    },
  ];

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const formValues = form.getFieldsValue();
      const searchParams = {
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        ...params,
      };

      if (formValues.orderCreateDate && formValues.orderCreateDate.length === 2) {
        searchParams.orderCreateDateStart = formValues.orderCreateDate[0].startOf('day').valueOf();
        searchParams.orderCreateDateEnd = formValues.orderCreateDate[1].endOf('day').valueOf();
      }
      if (formValues.orderNo) searchParams.orderNo = formValues.orderNo.trim();
      if (formValues.channel) searchParams.channel = formValues.channel;
      if (formValues.paymentMethod) searchParams.paymentMethod = formValues.paymentMethod;
      if (formValues.isTimeout !== undefined && formValues.isTimeout !== null) {
        searchParams.isTimeout = formValues.isTimeout;
      }
      if (formValues.orderEndStatus) searchParams.orderEndStatus = formValues.orderEndStatus;

      const response = await reportService.pageSLAFulfillment(searchParams);
      if (response.success) {
        const data = response.data || {};
        const rawList = data.tableData || [];
        setDataSource(rawList.map(normalizeRecord));
        setPagination((prev) => ({
          ...prev,
          current: data.paging?.currentPage || 1,
          total: data.paging?.totalCount || 0,
        }));
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('Query SLA report failed:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    reportService.getSlaChannelOptions().then(res => {
      if (res.success && res.data) {
        setChannelOptions(res.data.map(o => ({ value: o.value, label: o.label })));
      }
    });
    reportService.getSlaPaymentMethodOptions().then(res => {
      if (res.success && res.data) {
        setPaymentMethodOptions(res.data.map(o => ({ value: o.value, label: o.label })));
      }
    });
  }, []);

  const handleSearch = (values) => {
    form.setFieldsValue(values);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const formValues = form.getFieldsValue();
      const searchParams = {};
      if (formValues.orderCreateDate && formValues.orderCreateDate.length === 2) {
        searchParams.orderCreateDateStart = formValues.orderCreateDate[0].startOf('day').valueOf();
        searchParams.orderCreateDateEnd = formValues.orderCreateDate[1].endOf('day').valueOf();
      }
      if (formValues.orderNo) searchParams.orderNo = formValues.orderNo.trim();
      if (formValues.channel) searchParams.channel = formValues.channel;
      if (formValues.paymentMethod) searchParams.paymentMethod = formValues.paymentMethod;
      if (formValues.isTimeout !== undefined && formValues.isTimeout !== null) {
        searchParams.isTimeout = formValues.isTimeout;
      }
      if (formValues.orderEndStatus) searchParams.orderEndStatus = formValues.orderEndStatus;

      message.loading(t('common.message.exportingData') || 'Exporting...', 0);
      const response = await reportService.exportSLAFulfillment(searchParams);
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = response.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.destroy();
        message.success(t('common.message.exportSuccess') || 'Export successful');
      } else {
        message.destroy();
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('Export SLA report failed:', error);
      message.destroy();
      message.error(t('common.message.operationFailed'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));
    fetchData({
      currentPage: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <FilterPanel
        form={form}
        onFilter={handleSearch}
        onReset={handleReset}
        collapsible={true}
        defaultCollapsed={false}
        loading={loading}
      >
        <Col span={4}>
          <Form.Item name="orderNo" label={t('report.slaFulfillment.orderNo')}>
            <Input placeholder={t('common.pleaseInput')} allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="orderCreateDate" label={t('report.slaFulfillment.orderCreateDate')}>
            <RangePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="channel" label={t('report.slaFulfillment.channel')}>
            <Select placeholder={t('common.pleaseSelect')} options={channelOptions} allowClear />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="paymentMethod" label={t('report.slaFulfillment.paymentMethod')}>
            <Select placeholder={t('common.pleaseSelect')} options={paymentMethodOptions} allowClear />
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item name="isTimeout" label={t('report.slaFulfillment.isTimeout')}>
            <Select placeholder={t('common.pleaseSelect')} options={getIsTimeoutOptions(t)} allowClear />
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item name="orderEndStatus" label={t('report.slaFulfillment.orderEndStatus')}>
            <Select placeholder={t('common.pleaseSelect')} options={getOrderEndStatusOptions(t)} allowClear />
          </Form.Item>
        </Col>
      </FilterPanel>

      <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button icon={<ExportOutlined />} onClick={handleExport} loading={exportLoading}>{t('common.export')}</Button>
      </div>

      <Card style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="orderNo"
            scroll={{ x: 1400, y: scrollY }}
          />
        </div>
      </Card>
    </div>
  );
};

export default SLAFulfillmentReport;
